import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { getDueDates } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canAccess } from '../utils/permissions';
import { formatDateEuropean, toLocalDateString } from '../utils/dateUtils';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';

function nextYearMonth(dateStr) {
  const [year, month] = dateStr.split('-').map(Number);
  if (month === 12) return `${year + 1}-01`;
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function getDueStatus(expirationDate, todayStr) {
  if (!expirationDate || expirationDate < todayStr) return 'late';
  const thisMonth = todayStr.slice(0, 7);
  const itemMonth = expirationDate.slice(0, 7);
  if (itemMonth === thisMonth) return 'thisMonth';
  if (itemMonth === nextYearMonth(todayStr)) return 'nextMonth';
  return 'later';
}

function statusChipColor(status) {
  if (status === 'late') return 'error';
  if (status === 'thisMonth') return 'warning';
  if (status === 'nextMonth') return 'info';
  return 'default';
}

function dueDatePath(row) {
  if (row.source === 'personnel') {
    return `/personnel?open=${row.entityId}`;
  }
  if (row.source === 'machine') {
    const view = row.itemType === 'file' ? 'files' : 'edit';
    return `/warehouse?tab=machines&open=${row.entityId}&view=${view}`;
  }
  if (row.source === 'project') {
    return `/projects?open=${row.entityId}&tab=otherDocuments`;
  }
  if (row.source === 'financeQuote' || row.source === 'financeDelivery') {
    return `/finance/quotes?open=${row.entityId}`;
  }
  if (row.source === 'financeInvoice') {
    return `/finance/invoices?open=${row.entityId}`;
  }
  return null;
}

function DueDates() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');

  const canPersonnel = canAccess(user, 'personnel.read');
  const canMachines = canAccess(user, 'materials.read');
  const canProjects = canAccess(user, 'projects.read');
  const canFinance = canAccess(user, 'finance.quotes.read') || canAccess(user, 'finance.invoices.read') || canAccess(user, 'finance.deliveries.read');
  const todayStr = toLocalDateString(new Date());

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const response = await getDueDates();
        setItems(response.data || []);
      } catch (error) {
        console.error('Error fetching due dates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  const filteredItems = useMemo(() => {
    if (sourceFilter === 'all') return items;
    if (sourceFilter === 'personnel') return items.filter((item) => item.source === 'personnel');
    if (sourceFilter === 'machines') return items.filter((item) => item.source === 'machine');
    if (sourceFilter === 'projects') return items.filter((item) => item.source === 'project');
    if (sourceFilter === 'finance') return items.filter((item) => String(item.source || '').startsWith('finance'));
    return items;
  }, [items, sourceFilter]);

  const rows = useMemo(
    () => filteredItems.map((item) => ({
      ...item,
      status: getDueStatus(item.expirationDate, todayStr),
    })),
    [filteredItems, todayStr]
  );

  const counts = useMemo(() => ({
    late: rows.filter((row) => row.status === 'late').length,
    thisMonth: rows.filter((row) => row.status === 'thisMonth').length,
    nextMonth: rows.filter((row) => row.status === 'nextMonth').length,
    total: rows.length,
  }), [rows]);

  const handleOpenRow = (row) => {
    const path = dueDatePath(row);
    if (path) navigate(path);
  };

  const columns = [
    {
      field: 'status',
      headerName: t('dueDates.columns.status'),
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          label={t(`dueDates.status.${params.value}`)}
          color={statusChipColor(params.value)}
        />
      ),
    },
    {
      field: 'expirationDate',
      headerName: t('dueDates.columns.expirationDate'),
      width: 140,
      valueFormatter: (params) => formatDateEuropean(params.value ? `${params.value}T12:00:00` : params.value),
    },
    {
      field: 'source',
      headerName: t('dueDates.columns.source'),
      width: 130,
      valueGetter: (params) => t(`dueDates.sources.${params.row.source}`),
    },
    {
      field: 'entityName',
      headerName: t('dueDates.columns.name'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'category',
      headerName: t('dueDates.columns.category'),
      width: 160,
      valueGetter: (params) => params.row.category || '-',
    },
    {
      field: 'itemName',
      headerName: t('dueDates.columns.item'),
      flex: 1,
      minWidth: 180,
    },
  ];

  return (
    <ResponsivePageSection>
      <Typography variant="h4" sx={{ mb: 2 }}>{t('dueDates.title')}</Typography>

      <Typography variant="body1" sx={{ mb: 2 }}>
        <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
          {t('dueDates.summary.late')}: {counts.late}
        </Box>
        {', '}
        <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600 }}>
          {t('dueDates.summary.thisMonth')}: {counts.thisMonth}
        </Box>
        {', '}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {t('dueDates.summary.nextMonth')}: {counts.nextMonth}
        </Box>
        {', '}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {t('dueDates.summary.total')}: {counts.total}
        </Box>
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={sourceFilter}
        onChange={(_, value) => { if (value) setSourceFilter(value); }}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <ToggleButton value="all">{t('dueDates.filters.all')}</ToggleButton>
        {canPersonnel && (
          <ToggleButton value="personnel">{t('dueDates.filters.personnel')}</ToggleButton>
        )}
        {canMachines && (
          <ToggleButton value="machines">{t('dueDates.filters.machines')}</ToggleButton>
        )}
        {canProjects && (
          <ToggleButton value="projects">{t('dueDates.filters.projects')}</ToggleButton>
        )}
        {canFinance && (
          <ToggleButton value="finance">{t('dueDates.filters.finance')}</ToggleButton>
        )}
      </ToggleButtonGroup>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">{t('dueDates.empty')}</Typography>
      ) : isMobile ? (
        <ResponsiveDataList
          rows={rows}
          onRowClick={handleOpenRow}
          getTitle={(row) => row.entityName || t('common.notSet')}
          getSubtitle={(row) => `${formatDateEuropean(`${row.expirationDate}T12:00:00`)} · ${t(`dueDates.sources.${row.source}`)}`}
          renderFields={(row) => (
            <Box>
              <Typography variant="body2">
                <b>{t('dueDates.columns.item')}:</b> {row.itemName}
              </Typography>
              {row.category ? (
                <Typography variant="body2">
                  <b>{t('dueDates.columns.category')}:</b> {row.category}
                </Typography>
              ) : null}
              <Box sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={t(`dueDates.status.${row.status}`)}
                  color={statusChipColor(row.status)}
                />
              </Box>
            </Box>
          )}
        />
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          autoHeight
          disableRowSelectionOnClick
          onRowClick={(params) => handleOpenRow(params.row)}
          getRowClassName={(params) => (params.row.status === 'late' ? 'due-dates-row-late' : '')}
          localeText={{ noRowsLabel: t('dueDates.empty') }}
          initialState={{
            sorting: { sortModel: [{ field: 'expirationDate', sort: 'asc' }] },
          }}
          sx={{
            cursor: 'pointer',
            '& .due-dates-row-late': {
              color: 'error.main',
            },
          }}
        />
      )}
    </ResponsivePageSection>
  );
}

export default DueDates;
